import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PermissionNames } from 'src/app/modules/Settings/permissions/enum/permission-names.enum';
import { LocalStoreService } from './local-store.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissions: Set<string> = new Set();
  permissionsReady$ = new BehaviorSubject<boolean>(false);
  permissionsLoaded: boolean = false;

  constructor(private store: LocalStoreService) {
    // Try to load permissions from storage on service initialization
    this.initializeFromStorage();
  }

  private initializeFromStorage() {
    const storedPermissions = this.store.getItem("permissions");
    if (storedPermissions && Array.isArray(storedPermissions) && storedPermissions.length > 0) {
      console.log('Loading permissions from storage:', storedPermissions);
      this.permissions = new Set(storedPermissions);
      this.permissionsLoaded = true;
      this.permissionsReady$.next(true);
    }
  }

  storePermissions(permissions: string[]) {
    console.log('Storing permissions:', permissions);
    
    if (!permissions || !Array.isArray(permissions)) {
      console.warn('Invalid permissions array received');
      return;
    }

    // Store permissions in both memory and localStorage
    this.permissions = new Set(permissions);
    this.store.setItem("permissions", permissions);
    
    // Mark as loaded and notify subscribers
    this.permissionsLoaded = true;
    this.permissionsReady$.next(true);
    
    console.log('Permissions stored and ready signal sent');
  }

  // Check if permissions are already loaded
  arePermissionsLoaded(): boolean {
    return this.permissionsLoaded && this.permissions.size > 0;
  }

  // Force reload permissions from storage
  reloadFromStorage(): boolean {
    const storedPermissions = this.store.getItem("permissions");
    if (storedPermissions && Array.isArray(storedPermissions) && storedPermissions.length > 0) {
      this.permissions = new Set(storedPermissions);
      this.permissionsLoaded = true;
      this.permissionsReady$.next(true);
      return true;
    }
    return false;
  }

  // Check single permission
  isPermitted(permission: string | PermissionNames): boolean {
    return this.permissions.has(permission);
  }

  // Check multiple permissions
  hasAnyPermission(...perms: (string | PermissionNames)[]): boolean {
    return perms.some(p => this.permissions.has(p));
  }

  getAll(): string[] {
    return Array.from(this.permissions);
  }

  // Clear all permissions
  clearPermissions(): void {
    this.permissions.clear();
    this.store.setItem("permissions", []);
    this.permissionsLoaded = false;
    this.permissionsReady$.next(false);
  }
}